/* eslint-disable @typescript-eslint/no-unused-vars */
import { CloseIcon } from '@atoms/Icons/CloseIcon';
import { CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { ImageEvidence, LabelInput, NoDataText, UploadButton } from './styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import useGetEvidenceImages from 'src/features/SocialMediaLinks/api/get-evidence';
import { useParams } from 'react-router-dom';
import { useUpdateEvidence } from 'src/features/SocialMediaLinks/api/upload-evidence';
import { errorMessage, successMessage } from '@services';
import PreviewImageModal from './PreviewImageModal';

interface IEditNoteModalProps {
  open: boolean;
  onCancel: () => void;
  id: string;
  setId: (id: string) => void;
}

export const UploadEvidenceModal = ({ open, onCancel, id, setId }: IEditNoteModalProps) => {
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [currentImage, setCurrentImage] = useState<{
    id: string;
    imageUrl: string;
    date: string;
  }>();
  const [isImageLoading, setIsImageLoading] = useState(false);

  const { assetId } = useParams();
  const { data, refetch, isLoading } = useGetEvidenceImages(id, assetId || '');
  const [file, setFile] = useState<File | null>(null);
  const images = useMemo(() => {
    if (data?.length) {
      return data.map((item) => {
        return {
          id: item.id,
          imageUrl: item.url,
          date: new Date(item.createdAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
        };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (!isLoading && images.length > 0) {
      setCurrentImage(images[0]);
      setIsImageLoading(false);
      setIsImageLoading(true);
    }
    if (images.length === 0) {
      setCurrentImage(undefined);
    }

    return () => {
      setCurrentImage(undefined);
      setIsImageLoading(false);
    };
  }, [isLoading, images]);

  const handleNavigate = (direction: 'left' | 'right') => {
    if (!currentImage) return;
    const currentIndex = images.findIndex((img) => img.id === currentImage.id);
    if (currentIndex === -1) return;
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= images.length) return;
    setIsImageLoading(true);
    setCurrentImage(images[newIndex]);
  };

  const handleOnUpload = (e: SyntheticEvent<HTMLInputElement>) => {
    if (e.currentTarget.files?.length) {
      const file = e.currentTarget.files[0];
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExts = ['.jpeg', '.jpg', '.png'];
      const fileName = file.name.toLowerCase();

      if (!allowedTypes.includes(file.type) || !allowedExts.some((ext) => fileName.endsWith(ext))) {
        errorMessage('Only JPEG, JPG, PNG files are allowed!');
        e.currentTarget.value = '';
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        errorMessage('File size must be 10MB or less!');
        e.currentTarget.value = '';
        return;
      }

      const fileReader = new FileReader();
      const formData = new FormData();
      setFile(file);
      formData.append('file', file);
      fileReader.readAsDataURL(file);
      fileReader.onload = (event: ProgressEvent<FileReader>) => {
        if (event?.target?.result) {
          const previewImg = event.target?.result;
          setPreviewImg(previewImg as string);
          setOpenPreview(true);
        }
      };
    }
  };

  const uploadEvidenceMultation = useUpdateEvidence({
    mutationConfig: {
      onSuccess: () => {
        successMessage('Uploaded successfully!');
        setOpenPreview(false);
        setPreviewImg(null);
        setFile(null);
        refetch();
      },
      onError: () => {
        errorMessage('Upload failed!');
        setOpenPreview(false);
        setPreviewImg(null);
        setFile(null);
      },
    },
  });

  const handleUpload = () => {
    if (!file || !assetId || !id) {
      errorMessage('Please select a file to upload');
      return;
    }
    uploadEvidenceMultation.mutate({ data: file, modelId: assetId, id });
  };
  const handleCalcelUpload = () => {
    setOpenPreview(false);
    setPreviewImg(null);
    setFile(null);
  };

  const handleClose = () => {
    setOpenPreview(false);
    setPreviewImg(null);
    setFile(null);
    setIsImageLoading(false);
    setCurrentImage(undefined);
    setId('');
    onCancel();
  };

  const handleDownloadImage = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'evidence-image';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onClickLeft = () => {
    if (!images.length || !currentImage || currentImage.id === images[0]?.id) return;
    handleNavigate('left');
  };

  const onClickRight = () => {
    if (!images.length || !currentImage || currentImage.id === images[images.length - 1]?.id)
      return;
    handleNavigate('right');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '10px',
          minWidth: '596px',
          height: '787px',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box py="24px" px="32px" height="100%" width="100%">
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h5"></Typography>
            <IconButton
              onClick={onCancel}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '24px',
                lineHeight: '20px',
                color: '#464F60',
                whiteSpace: 'normal',
                marginBottom: '32px',
              }}
            >
              Your Evidence
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '16px',
                lineHeight: '20px',
                color: '#464F60',
                whiteSpace: 'normal',
                marginBottom: '18px',
                fontWeight: 500,
              }}
            >
              Screenshot
            </Typography>
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.7)',
                  zIndex: 1301,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            <Box sx={{ marginBottom: '84px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Box onClick={onClickLeft}>
                  {images.length > 0 && currentImage?.id !== images[0]?.id ? (
                    <ArrowBackIosIcon
                      sx={{
                        color: '#44AEFB',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    ></Box>
                  )}
                </Box>
                {currentImage ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    {isImageLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'rgba(255,255,255,0.7)',
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    )}
                    <ImageEvidence
                      src={currentImage.imageUrl}
                      alt="image-evidence"
                      loading="lazy"
                      onLoad={() => setIsImageLoading(false)}
                      style={
                        isImageLoading ? { opacity: 0 } : { opacity: 1, transition: 'opacity 0.2s' }
                      }
                    />
                  </Box>
                ) : (
                  !isLoading && <NoDataText>No image available</NoDataText>
                )}

                <Box
                  onClick={onClickRight}
                  sx={{
                    cursor: 'pointer',
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {images.length > 0 && currentImage?.id !== images[images.length - 1]?.id ? (
                    <ArrowForwardIosIcon
                      sx={{
                        color: '#44AEFB',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    ></Box>
                  )}
                </Box>
              </Box>
              <Box
                display={'flex'}
                justifyContent="space-between"
                mt="20px"
                sx={{ opacity: images.length ? 1 : 0 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <DoneAllIcon sx={{ color: '#44AEFB' }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#464F60',
                      whiteSpace: 'normal',
                    }}
                  >
                    {currentImage?.date}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
                  onClick={() => handleDownloadImage(currentImage?.imageUrl || '')}
                >
                  <ImportExportIcon sx={{ color: '#44AEFB' }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#44AEFB',
                      whiteSpace: 'normal',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Download
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ paddingBottom: '32px' }}>
              <LabelInput htmlFor="upload-evidence">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="upload-evidence"
                  multiple={false}
                  onChange={handleOnUpload}
                />
                <UploadButton>Upload Screenshot</UploadButton>
              </LabelInput>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <PreviewImageModal
        open={openPreview}
        url={previewImg || ''}
        handleClose={handleCalcelUpload}
        handleUpload={handleUpload}
        loading={uploadEvidenceMultation.isLoading}
      />
    </Dialog>
  );
};
